import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TrendingUp, Clock, Home, DollarSign, AlertCircle } from 'lucide-react';

const LMIvsWaitCalculator = () => {
  // Property & Loan Details
  const [propertyPrice, setPropertyPrice] = useState(600000);
  const [currentDeposit, setCurrentDeposit] = useState(60000); // 10%
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  
  // Savings & Growth
  const [monthlySavings, setMonthlySavings] = useState(2000);
  const [propertyGrowthRate, setPropertyGrowthRate] = useState(5); // Annual %
  const [monthlyRent, setMonthlyRent] = useState(2400);
  
  // LMI
  const [lmiRate, setLmiRate] = useState(2.5); // % of loan amount

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculateScenarios = () => {
    // Scenario 1: Buy Now (with LMI if LVR > 80%)
    const loanAmountNow = propertyPrice - currentDeposit;
    const lvrNow = (loanAmountNow / propertyPrice) * 100;
    const lmiCost = lvrNow > 80 ? (loanAmountNow * lmiRate / 100) : 0;
    const totalLoanNow = loanAmountNow + lmiCost;
    
    const monthlyRateNow = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    const monthlyPaymentNow = totalLoanNow * (monthlyRateNow * Math.pow(1 + monthlyRateNow, numberOfPayments)) / 
                             (Math.pow(1 + monthlyRateNow, numberOfPayments) - 1);
    
    const totalPaidNow = monthlyPaymentNow * numberOfPayments;
    const totalInterestNow = totalPaidNow - totalLoanNow;
    
    // Scenario 2: Wait to Save 20% Deposit
    const targetDeposit = propertyPrice * 0.2;
    const depositNeeded = targetDeposit - currentDeposit;
    const monthsToSave = Math.ceil(depositNeeded / monthlySavings);
    const yearsToSave = (monthsToSave / 12).toFixed(1);
    
    // Property price after waiting (with appreciation)
    const monthlyGrowthRate = Math.pow(1 + propertyGrowthRate / 100, 1/12) - 1;
    const futurePriceWait = propertyPrice * Math.pow(1 + monthlyGrowthRate, monthsToSave);
    
    // Final deposit after saving
    const finalDeposit = currentDeposit + (monthlySavings * monthsToSave);
    
    // Loan amount when buying later
    const loanAmountWait = futurePriceWait - finalDeposit;
    const lvrWait = (loanAmountWait / futurePriceWait) * 100;
    
    const monthlyPaymentWait = loanAmountWait * (monthlyRateNow * Math.pow(1 + monthlyRateNow, numberOfPayments)) / 
                              (Math.pow(1 + monthlyRateNow, numberOfPayments) - 1);
    
    const totalPaidWait = monthlyPaymentWait * numberOfPayments;
    const totalInterestWait = totalPaidWait - loanAmountWait;
    
    // Rent paid while waiting
    const totalRentPaid = monthlyRent * monthsToSave;
    
    // Total cost comparison
    const totalCostNow = propertyPrice + lmiCost + totalInterestNow;
    const totalCostWait = futurePriceWait + totalInterestWait + totalRentPaid;
    
    const difference = totalCostWait - totalCostNow;
    const betterOption = difference > 0 ? 'Buy Now' : 'Wait';
    
    // Equity comparison after 5 years
    const monthsElapsed = 60; // 5 years
    
    // Buy Now equity
    const futurePropertyValueNow = propertyPrice * Math.pow(1 + propertyGrowthRate / 100, 5);
    let balanceNow = totalLoanNow;
    for (let i = 0; i < monthsElapsed; i++) {
      const interest = balanceNow * monthlyRateNow;
      const principal = monthlyPaymentNow - interest;
      balanceNow -= principal;
    }
    const equityNow = futurePropertyValueNow - balanceNow;
    
    // Wait equity (only if they've bought by then)
    let equityWait = 0;
    let futurePropertyValueWait = 0;
    if (monthsToSave < monthsElapsed) {
      const monthsOwned = monthsElapsed - monthsToSave;
      futurePropertyValueWait = futurePriceWait * Math.pow(1 + propertyGrowthRate / 100, monthsOwned / 12);
      let balanceWait = loanAmountWait;
      for (let i = 0; i < monthsOwned; i++) {
        const interest = balanceWait * monthlyRateNow;
        const principal = monthlyPaymentWait - interest;
        balanceWait -= principal;
      }
      equityWait = futurePropertyValueWait - balanceWait;
    }

    return {
      buyNow: {
        propertyPrice,
        deposit: currentDeposit,
        loanAmount: loanAmountNow,
        lvr: lvrNow,
        lmi: lmiCost,
        totalLoan: totalLoanNow,
        monthlyPayment: monthlyPaymentNow,
        totalInterest: totalInterestNow,
        totalCost: totalCostNow,
        equity5Years: equityNow,
        propertyValue5Years: futurePropertyValueNow
      },
      wait: {
        monthsToSave,
        yearsToSave,
        futurePrice: futurePriceWait,
        finalDeposit,
        loanAmount: loanAmountWait,
        lvr: lvrWait,
        lmi: 0,
        monthlyPayment: monthlyPaymentWait,
        totalInterest: totalInterestWait,
        rentPaid: totalRentPaid,
        totalCost: totalCostWait,
        equity5Years: equityWait,
        propertyValue5Years: futurePropertyValueWait,
        priceIncrease: futurePriceWait - propertyPrice
      },
      comparison: {
        difference: Math.abs(difference),
        betterOption,
        savingsByBuyingNow: difference > 0 ? difference : 0,
        savingsByWaiting: difference < 0 ? Math.abs(difference) : 0
      }
    };
  };

  const results = calculateScenarios();

  return (
    <div>
      <Header />
      <div className="min-h-screen bg-page py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Buy Now or Wait Calculator
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Compare the cost of buying now (with LMI) versus waiting to save a 20% deposit
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {/* Input Section */}
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Property & Loan</CardTitle>
                  <CardDescription>Current property details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Property Price ($)</Label>
                    <Input 
                      type="number"
                      value={propertyPrice || ''}
                      onChange={(e) => setPropertyPrice(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Current Deposit ($)</Label>
                    <Input 
                      type="number"
                      value={currentDeposit || ''}
                      onChange={(e) => setCurrentDeposit(parseFloat(e.target.value) || 0)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {((currentDeposit / propertyPrice) * 100).toFixed(1)}% of property price
                    </p>
                  </div>
                  <div>
                    <Label>Interest Rate (% p.a.)</Label>
                    <Input 
                      type="number"
                      step="0.1"
                      value={interestRate || ''}
                      onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Loan Term (years)</Label>
                    <Input 
                      type="number"
                      value={loanTerm || ''}
                      onChange={(e) => setLoanTerm(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>LMI Rate (% of loan)</Label>
                    <Input 
                      type="number"
                      step="0.1"
                      value={lmiRate || ''}
                      onChange={(e) => setLmiRate(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Savings & Growth</CardTitle>
                  <CardDescription>Your savings capacity</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Monthly Savings ($)</Label>
                    <Input 
                      type="number"
                      value={monthlySavings || ''}
                      onChange={(e) => setMonthlySavings(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Property Growth Rate (% p.a.)</Label>
                    <Input 
                      type="number"
                      step="0.1"
                      value={propertyGrowthRate || ''}
                      onChange={(e) => setPropertyGrowthRate(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Monthly Rent ($)</Label>
                    <Input 
                      type="number"
                      value={monthlyRent || ''}
                      onChange={(e) => setMonthlyRent(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Results Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Winner Card */}
              <Card className={`border-4 ${results.comparison.betterOption === 'Buy Now' ? 'border-green-500 bg-green-50' : 'border-blue-500 bg-blue-50'}`}>
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-3">
                    {results.comparison.betterOption === 'Buy Now' ? (
                      <Home className="h-8 w-8 text-green-600" />
                    ) : (
                      <Clock className="h-8 w-8 text-blue-600" />
                    )}
                    Recommended: {results.comparison.betterOption}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <div className="text-lg text-gray-700 mb-2">Potential Savings</div>
                    <div className={`text-4xl font-bold ${results.comparison.betterOption === 'Buy Now' ? 'text-green-600' : 'text-blue-600'}`}>
                      {formatCurrency(results.comparison.difference)}
                    </div>
                    <p className="text-sm text-gray-600 mt-4">
                      {results.comparison.betterOption === 'Buy Now' 
                        ? 'Based on property appreciation, buying now saves you money despite paying LMI'
                        : 'Waiting to save 20% deposit will save you money overall'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Scenario Comparison */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Buy Now */}
                <Card className="border-2 border-green-500">
                  <CardHeader className="bg-green-50">
                    <CardTitle className="flex items-center gap-2">
                      <Home className="h-5 w-5" />
                      Buy Now
                    </CardTitle>
                    <CardDescription>With {results.buyNow.lvr.toFixed(1)}% LVR</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Property Price:</span>
                      <span className="font-semibold">{formatCurrency(results.buyNow.propertyPrice)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Deposit:</span>
                      <span className="font-semibold">{formatCurrency(results.buyNow.deposit)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">LMI Cost:</span>
                      <span className="font-semibold text-red-600">{formatCurrency(results.buyNow.lmi)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t pt-2">
                      <span className="text-gray-600">Total Loan:</span>
                      <span className="font-semibold">{formatCurrency(results.buyNow.totalLoan)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monthly Payment:</span>
                      <span className="font-semibold">{formatCurrency(results.buyNow.monthlyPayment)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Interest:</span>
                      <span className="font-semibold text-red-600">{formatCurrency(results.buyNow.totalInterest)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Rent Paid:</span>
                      <span className="font-semibold">$0</span>
                    </div>
                    <div className="bg-green-100 p-3 rounded-lg mt-3">
                      <div className="flex justify-between text-sm font-bold">
                        <span>Total Cost:</span>
                        <span className="text-green-700">{formatCurrency(results.buyNow.totalCost)}</span>
                      </div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Equity in 5 years:</div>
                      <div className="font-bold text-blue-600">{formatCurrency(results.buyNow.equity5Years)}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Wait */}
                <Card className="border-2 border-blue-500">
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Wait {results.wait.yearsToSave} Years
                    </CardTitle>
                    <CardDescription>Save to 20% deposit</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Future Price:</span>
                      <span className="font-semibold">{formatCurrency(results.wait.futurePrice)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Price Increase:</span>
                      <span className="font-semibold text-orange-600">+{formatCurrency(results.wait.priceIncrease)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Final Deposit:</span>
                      <span className="font-semibold">{formatCurrency(results.wait.finalDeposit)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">LMI Cost:</span>
                      <span className="font-semibold text-green-600">$0</span>
                    </div>
                    <div className="flex justify-between text-sm border-t pt-2">
                      <span className="text-gray-600">Total Loan:</span>
                      <span className="font-semibold">{formatCurrency(results.wait.loanAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monthly Payment:</span>
                      <span className="font-semibold">{formatCurrency(results.wait.monthlyPayment)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Interest:</span>
                      <span className="font-semibold text-red-600">{formatCurrency(results.wait.totalInterest)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Rent Paid:</span>
                      <span className="font-semibold text-red-600">{formatCurrency(results.wait.rentPaid)}</span>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-lg mt-3">
                      <div className="flex justify-between text-sm font-bold">
                        <span>Total Cost:</span>
                        <span className="text-blue-700">{formatCurrency(results.wait.totalCost)}</span>
                      </div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Equity in 5 years:</div>
                      <div className="font-bold text-blue-600">
                        {results.wait.monthsToSave >= 60 ? 'Not yet purchased' : formatCurrency(results.wait.equity5Years)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Important Notes */}
              <Card className="border-l-4 border-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                    Important Considerations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex gap-2">
                      <span className="text-orange-600">•</span>
                      <span>This calculation assumes {propertyGrowthRate}% annual property growth. Actual growth may vary significantly by location and market conditions.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-orange-600">•</span>
                      <span>LMI can be paid upfront or added to your loan (which increases interest paid over time).</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-orange-600">•</span>
                      <span>This comparison doesn't include stamp duty, legal fees, or ongoing property costs.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-orange-600">•</span>
                      <span>Your personal circumstances, risk tolerance, and financial goals should guide your decision.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LMIvsWaitCalculator;
